import { fetchAuthSession } from 'aws-amplify/auth';
import { config } from '../../config/config';

// Refresh a little before expiry so Katon Meet never receives a token that dies mid-request
const REFRESH_BEFORE_EXPIRY_MS = 2 * 60 * 1000;

export interface KatonMeetToken {
  accessToken: string;
  // Expiry time in milliseconds since epoch
  expiresAt: number;
  // Lets Katon Meet refresh the access token itself (same Cognito user pool and app client)
  refreshToken?: string;
}

let pendingToken: Promise<KatonMeetToken> | null = null;

// fetchAuthSession() doesn't expose the refresh token, so read it where Amplify stores it
const readRefreshToken = () => {
  try {
    const prefix = `CognitoIdentityServiceProvider.${config.amplifyConfig.aws_user_pools_web_client_id}`;
    const username = localStorage.getItem(`${prefix}.LastAuthUser`);
    return (username && localStorage.getItem(`${prefix}.${username}.refreshToken`)) || undefined;
  } catch {
    return undefined;
  }
};

const readAccessToken = async (forceRefresh: boolean): Promise<KatonMeetToken | null> => {
  const session = await fetchAuthSession(forceRefresh ? { forceRefresh: true } : undefined);
  const token = session.tokens?.accessToken;
  const exp = Number(token?.payload?.exp);
  // Read after fetching, so a refresh token rotated by the refresh above is the one sent
  return token && exp ? { accessToken: token.toString(), expiresAt: exp * 1000, refreshToken: readRefreshToken() } : null;
};

// The current user's Cognito tokens for Katon Meet (same user pool and app client).
// The ID token is never shared. Concurrent callers share one request.
export const getKatonMeetToken = (): Promise<KatonMeetToken> => {
  if (!pendingToken) {
    pendingToken = (async () => {
      let token = await readAccessToken(false);
      if (!token || token.expiresAt - Date.now() < REFRESH_BEFORE_EXPIRY_MS) {
        token = await readAccessToken(true);
      }
      if (!token) {
        throw new Error('No active session');
      }

      return token;
    })().finally(() => {
      pendingToken = null;
    });
  }
  return pendingToken;
};
